import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography, Menu, MenuItem } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ReservaEstacionamiento } from "../../../types/ReservaEstacionamiento";
import { isToday } from "date-fns";
import { alpha } from "@mui/material/styles";
import EstacionamientoBlock from "../../reservations/components/EstacionamientoBlock";
import { ReservaEstacionamientoDisplay } from "../../../types/ReservaEstacionamiento";
import { useBreakpoint } from "../../../hooks/useBreakpoint";
import { formatDateKey, isDateOnlyInRange } from "../../shared/utils/dateUtils";

export const isReservaEstacionamiento = (
  reserva: unknown
): reserva is ReservaEstacionamientoDisplay => {
  if (!reserva || typeof reserva !== "object") return false;
  return "estacionamiento" in reserva && !("departamento" in reserva);
};

interface EstacionamientoRowProps {
  estacionamiento: string;
  rowHeight: number;
  dayColumnWidth: number;
  diasDelCalendario: Date[];
  reservas: ReservaEstacionamiento[];
  calcularPosicionReservaPx: (
    reserva: ReservaEstacionamiento,
    dias: Date[],
    dayWidth: number,
    rowHeight: number,
    rowIndex: number
  ) => {
    leftPx: number;
    widthPx: number;
    topPx: number;
    startsOutsideMonth: boolean;
    endsOutsideMonth: boolean;
  };
  onReservationClick: (reserva: ReservaEstacionamiento) => void;
  onCellClick: (
    fecha: Date,
    departamento: string | undefined,
    estacionamiento: string
  ) => void;
}

const EstacionamientoRow: React.FC<EstacionamientoRowProps> = ({
  estacionamiento,
  rowHeight,
  dayColumnWidth,
  diasDelCalendario,
  reservas,
  calcularPosicionReservaPx,
  onReservationClick,
  onCellClick,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedCellDate, setSelectedCellDate] = useState<Date | null>(null);
  const { isMobile } = useBreakpoint();
  const todayCellBg = alpha(
    theme.palette.primary.main,
    theme.palette.mode === "dark" ? 0.34 : 0.22
  );

  const reservasDelEstacionamiento = useMemo(
    () =>
      reservas.filter(
        (reserva) => reserva && reserva.estacionamiento === estacionamiento
      ),
    [reservas, estacionamiento]
  );

  const fechasConReservaActiva = useMemo(() => {
    const fechas = new Set<string>();

    for (const dia of diasDelCalendario) {
      const fechaKey = formatDateKey(dia);
      const tieneReserva = reservasDelEstacionamiento.some((reserva) =>
        isDateOnlyInRange(dia, reserva.entrada, reserva.salida)
      );
      if (tieneReserva) fechas.add(fechaKey);
    }

    return fechas;
  }, [diasDelCalendario, reservasDelEstacionamiento]);

  const tieneReservaActivaEnFecha = useCallback(
    (fecha: Date) => fechasConReservaActiva.has(formatDateKey(fecha)),
    [fechasConReservaActiva]
  );

  const handleCellClick = (e: React.MouseEvent, dia: Date) => {
    if (e.target instanceof Element && e.target.closest(".reservation-block")) {
      return;
    }

    if (isMobile) {
      if (!tieneReservaActivaEnFecha(dia)) {
        onCellClick(dia, undefined, estacionamiento);
      }
      return;
    }

    if (!tieneReservaActivaEnFecha(dia)) {
      setSelectedCellDate(dia);
      setMenuAnchor(e.currentTarget);
    }
  };

  const handleMenuItemClick = () => {
    if (selectedCellDate) {
      onCellClick(selectedCellDate, undefined, estacionamiento);
    }
    setMenuAnchor(null);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: rowHeight,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {/* Columna de estacionamiento */}
      <Box
        sx={{
          width: "70px",
          flexShrink: 0,
          p: 1,
          borderRight: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "sticky",
          left: 0,
          zIndex: 10,
          backgroundColor: theme.palette.background.paper,
          boxShadow: "1px 0 2px rgba(0,0,0,0.05)",
        }}
      >
        <Typography variant="body2" noWrap>
          {estacionamiento}
        </Typography>
      </Box>

      {/* Contenedor de celdas y reservas */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          position: "relative",
          touchAction: "pan-x pan-y",
        }}
      >
        {/* Celdas de días */}
        {diasDelCalendario.map((dia, idx) => {
          const tieneReserva = tieneReservaActivaEnFecha(dia);
          const esHoy = isToday(dia);
          const esFinDeSemana = dia.getDay() === 0 || dia.getDay() === 6;

          return (
            <Box
              key={idx}
              onClick={(e: React.MouseEvent) => handleCellClick(e, dia)}
              sx={{
                width: dayColumnWidth,
                minWidth: dayColumnWidth,
                height: "100%",
                borderRight: "1px solid",
                borderColor: "divider",
                cursor: "default",
                bgcolor: esHoy
                  ? todayCellBg
                  : esFinDeSemana
                  ? theme.palette.action.hover
                  : theme.palette.background.paper,
                "&:hover": !tieneReserva
                  ? {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05),
                    }
                  : {},
                touchAction: "pan-x pan-y",
                position: "relative",
                zIndex: 1,
              }}
            />
          );
        })}

        {/* Contenedor de reservas */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: "none",
            zIndex: 2,
          }}
        >
          {reservasDelEstacionamiento.map((reserva) => {
            const {
              leftPx,
              widthPx,
              topPx,
              startsOutsideMonth,
              endsOutsideMonth,
            } = calcularPosicionReservaPx(
              reserva,
              diasDelCalendario,
              dayColumnWidth,
              rowHeight,
              0
            );

            return (
              <Box
                key={reserva.id}
                sx={{
                  position: "absolute",
                  left: `${leftPx}px`,
                  width: `${widthPx}px`,
                  top: `${topPx}px`,
                  height: "100%",
                  pointerEvents: "auto",
                  padding: "2px",
                }}
              >
                <EstacionamientoBlock
                  reserva={reserva}
                  startsOutsideMonth={startsOutsideMonth}
                  endsOutsideMonth={endsOutsideMonth}
                  onClick={() => onReservationClick(reserva)}
                />
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Menú contextual */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "center",
          horizontal: "center",
        }}
      >
        <MenuItem onClick={handleMenuItemClick}>Nueva reserva</MenuItem>
      </Menu>
    </Box>
  );
};

export default React.memo(EstacionamientoRow);
